import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import {
  getLastUndoRedoProjectId,
  getUndoRedoSnapshot,
  redoLastProjectChange,
  refreshUndoRedoAvailability,
  setLastUndoRedoProjectId,
  subscribeUndoRedoStore,
  undoLastProjectChange,
} from '@/features/project-change-history/projectUndoRedoStore';

export {
  getLastUndoRedoProjectId,
  setLastUndoRedoProjectId,
} from '@/features/project-change-history/projectUndoRedoStore';

export function useProjectUndoRedo(projectId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();
  const snapshot = useSyncExternalStore(
    subscribeUndoRedoStore,
    getUndoRedoSnapshot,
    getUndoRedoSnapshot
  );
  const effectiveProjectId = projectId ?? snapshot.projectId ?? getLastUndoRedoProjectId();

  useEffect(() => {
    if (projectId) {
      setLastUndoRedoProjectId(projectId);
      void refreshUndoRedoAvailability(queryClient, projectId).catch(() => {});
      return;
    }
    const current = getLastUndoRedoProjectId();
    if (current) {
      void refreshUndoRedoAvailability(queryClient, current).catch(() => {});
    }
  }, [effectiveProjectId, projectId, queryClient]);

  const undo = useCallback(() => {
    void undoLastProjectChange(queryClient, effectiveProjectId)
      .then((res) => {
        if (!res.ok) {
          if (res.error_code === 'non_reversible') {
            showError(res.message ?? 'Невозможно отменить удаление файла');
          } else {
            showError(res.message ?? 'Нет изменений для отмены');
          }
          return;
        }
        showSuccess('Изменение отменено');
      })
      .catch((e: Error) => showError(e.message));
  }, [effectiveProjectId, queryClient, showError, showSuccess]);

  const redo = useCallback(() => {
    void redoLastProjectChange(queryClient, effectiveProjectId)
      .then((res) => {
        if (!res.ok) {
          showError(res.message ?? 'Нет изменений для повтора');
          return;
        }
        showSuccess('Изменение повторено');
      })
      .catch((e: Error) => showError(e.message));
  }, [effectiveProjectId, queryClient, showError, showSuccess]);

  return {
    canUndo: snapshot.canUndo,
    canRedo: snapshot.canRedo,
    undoPreview: snapshot.undoPreview,
    redoPreview: snapshot.redoPreview,
    undo,
    redo,
    isUndoing: snapshot.isUndoing,
    isRedoing: snapshot.isRedoing,
    effectiveProjectId,
  };
}

/** Физическая клавиша Z — не зависит от раскладки (на RU e.key === 'я'). */
function isUndoRedoShortcutKey(e: KeyboardEvent): boolean {
  return e.code === 'KeyZ' || e.key === 'z' || e.key === 'Z';
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    target.isContentEditable
  );
}

export function useProjectUndoRedoShortcuts(
  opts: {
    enabled?: boolean;
    projectId?: string | null;
  } = {}
) {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();
  const enabled = opts.enabled ?? true;
  const projectId = opts.projectId;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (!isUndoRedoShortcutKey(e)) return;
      if (isTextEditingTarget(e.target)) return;

      e.preventDefault();
      const effectiveId = projectId ?? getLastUndoRedoProjectId();
      if (e.shiftKey) {
        void redoLastProjectChange(queryClient, effectiveId)
          .then((res) => {
            if (!res.ok) {
              showError(res.message ?? 'Нет изменений для повтора');
              return;
            }
            showSuccess('Изменение повторено');
          })
          .catch((err: Error) => showError(err.message));
        return;
      }
      void undoLastProjectChange(queryClient, effectiveId)
        .then((res) => {
          if (!res.ok) {
            if (res.error_code === 'non_reversible') {
              showError(res.message ?? 'Невозможно отменить удаление файла');
            } else {
              showError(res.message ?? 'Нет изменений для отмены');
            }
            return;
          }
          showSuccess('Изменение отменено');
        })
        .catch((err: Error) => showError(err.message));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, projectId, queryClient, showError, showSuccess]);
}
